import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAtom } from 'jotai';
import { persistentLanguageAtom } from '../atoms/languageAtom';

import { headerNav } from '../constants';

const Header = () => {
  const [show, setShow] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [language, setLanguage] = useAtom(persistentLanguageAtom);
  const menuRef = useRef(null); // 메뉴 참조 추가
  const scrollPosition = useRef(0); // 스크롤 위치 저장용 ref
  const lastScrollTop = useRef(0);
  const lastTouchY = useRef(0); // 마지막 터치 위치 저장용 ref

  // 터치 이벤트 핸들러 - 메뉴 영역 외 터치 제어 (개선된 버전)
  const preventTouch = useCallback((e) => {
    // 메뉴 내부에서 발생한 이벤트는 항상 허용
    if (menuRef.current && menuRef.current.contains(e.target)) {
      // 터치 시작 위치 저장
      lastTouchY.current = e.touches[0].clientY;
      return;
    }

    // 오버레이 영역 클릭은 허용 (메뉴 닫기 위함)
    if (e.target.classList.contains('menu-overlay')) {
      return;
    }

    // 햄버거 메뉴 버튼 클릭은 허용
    if (
      e.target.closest('.header__nav__mobile') ||
      e.target.classList.contains('header__nav__mobile')
    ) {
      return;
    }

    // 헤더 로고 클릭은 허용
    if (
      e.target.closest('.header__logo') ||
      e.target.classList.contains('header__logo')
    ) {
      return;
    }
  }, []);

  // 터치 이벤트 핸들러 - 메뉴 내부만 스크롤 허용 (개선된 버전)
  const handleTouchMove = useCallback(
    (e) => {
      // 메뉴 내부에서 발생한 이벤트는 허용
      if (menuRef.current && menuRef.current.contains(e.target)) {
        // 메뉴 내부에서 스크롤이 최상단이나 최하단에 도달했을 때 더 이상 스크롤되지 않도록 방지
        const menuElement = menuRef.current.querySelector('ul');
        if (menuElement) {
          const isAtTop = menuElement.scrollTop <= 0;
          const isAtBottom =
            menuElement.scrollHeight - menuElement.scrollTop <=
            menuElement.clientHeight + 1;

          // 현재 터치 위치
          const currentTouchY = e.touches[0].clientY;
          // 터치 방향 (양수: 아래로 이동, 음수: 위로 이동)
          const touchDelta = currentTouchY - lastTouchY.current;

          // 위로 스크롤하려고 할 때 (손가락을 아래로 움직임) 이미 최상단이면 방지
          // 아래로 스크롤하려고 할 때 (손가락을 위로 움직임) 이미 최하단이면 방지
          if ((isAtTop && touchDelta > 0) || (isAtBottom && touchDelta < 0)) {
            e.preventDefault();
          }

          // 현재 위치를 마지막 위치로 업데이트
          lastTouchY.current = currentTouchY;
        }
        return;
      }

      // 오버레이 영역 터치는 허용
      if (e.target.classList.contains('menu-overlay')) {
        return;
      }

      // 햄버거 메뉴 버튼 터치는 허용
      if (
        e.target.closest('.header__nav__mobile') ||
        e.target.classList.contains('header__nav__mobile')
      ) {
        return;
      }

      // 헤더 로고 터치는 허용
      if (
        e.target.closest('.header__logo') ||
        e.target.classList.contains('header__logo')
      ) {
        return;
      }

      // 메뉴가 열려있을 때는 본문 스크롤 방지
      if (show) {
        e.preventDefault();
      }
    },
    [show]
  );

  // 휠 이벤트 핸들러 - 메뉴 내부만 스크롤 허용
  const preventWheel = useCallback(
    (e) => {
      // 메뉴 내부에서 발생한 이벤트는 허용
      if (menuRef.current && menuRef.current.contains(e.target)) {
        return;
      }

      // 메뉴가 열려있을 때는 본문 스크롤 방지
      if (show) {
        e.preventDefault();
      }
    },
    [show]
  );

  // 메뉴 상태가 변경될 때마다 이벤트 리스너 등록/해제
  useEffect(() => {
    // 메뉴 클릭 여부를 추적하는 변수
    let menuItemClicked = false;
    // 언어 변경 버튼 클릭 여부를 추적하는 변수
    let languageButtonClicked = false;

    // 언어 변경 버튼 클릭 이벤트 감지 함수 - 전역으로 정의
    const handleLanguageButtonClick = () => {
      languageButtonClicked = true;
    };

    if (show) {
      // 현재 스크롤 위치 저장
      scrollPosition.current = window.pageYOffset;

      // 메뉴가 열렸을 때 이벤트 리스너 등록 - passive: false로 설정하여 preventDefault 허용
      document.addEventListener('touchmove', handleTouchMove, {
        passive: false,
      });
      document.addEventListener('touchstart', preventTouch, { passive: false });

      // 휠 이벤트는 메뉴 내부만 허용
      document.addEventListener('wheel', preventWheel, { passive: false });

      // body 스타일 설정 - position: fixed와 top 속성을 사용하지 않음
      // 이렇게 하면 스크롤 위치가 유지됨
      document.body.style.overflow = 'hidden';
      // document.body.style.width = '100%';
      // document.body.style.height = '100%';
      document.body.classList.add('menu-open');

      // HTML 요소에 no-scroll 클래스 추가
      document.documentElement.classList.add('no-scroll');

      // iOS에서 추가 설정 - position: fixed 사용하지 않음
      document.documentElement.style.overflow = 'hidden';
      // document.documentElement.style.height = '100%';

      // 메뉴 항목 클릭 이벤트 감지 함수
      const handleMenuItemClick = () => {
        menuItemClicked = true;
      };

      // 메뉴 항목에 클릭 이벤트 리스너 추가
      const menuItems = menuRef.current?.querySelectorAll(
        'li:not(.language-toggle)'
      );
      menuItems?.forEach((item) => {
        item.addEventListener('click', handleMenuItemClick);
      });

      // 언어 변경 버튼에 클릭 이벤트 리스너 추가
      const languageButtons = menuRef.current?.querySelectorAll(
        '.language-toggle button'
      );
      languageButtons?.forEach((button) => {
        button.addEventListener('click', handleLanguageButtonClick);
      });

      return () => {
        // 메뉴 항목에서 클릭 이벤트 리스너 제거
        menuItems?.forEach((item) => {
          item.removeEventListener('click', handleMenuItemClick);
        });

        // 언어 변경 버튼에서 클릭 이벤트 리스너 제거
        languageButtons?.forEach((button) => {
          button.removeEventListener('click', handleLanguageButtonClick);
        });

        // 메뉴가 닫힐 때 이벤트 리스너 해제
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchstart', preventTouch);
        document.removeEventListener('wheel', preventWheel);

        // 메뉴 항목을 클릭한 경우나 언어 변경 버튼을 클릭한 경우에는 스크롤 위치를 복원하지 않음
        // 언어 변경 버튼 클릭 시에는 handleLanguageChange 함수에서 별도로 처리
        if (!menuItemClicked && !languageButtonClicked) {
          // 스크롤 위치 복원 로직은 toggleMenu 함수에서 처리
          // 여기서는 추가 처리가 필요한 경우에만 실행
        }
      };
    } else {
      // 메뉴가 닫혔을 때 이벤트 리스너 해제
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchstart', preventTouch);
      document.removeEventListener('wheel', preventWheel);
    }

    // 컴포넌트 언마운트 시 이벤트 리스너 제거 및 스타일 초기화
    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchstart', preventTouch);
      document.removeEventListener('wheel', preventWheel);
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.paddingRight = '';
      document.documentElement.classList.remove('no-scroll');
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
    };
  }, [show, handleTouchMove, preventTouch, preventWheel]);

  const handleLanguageChange = (lang) => {
    // 이미 선택된 언어와 같은 경우 아무 작업도 하지 않음
    if (language === lang) return;

    // 언어 변경
    setLanguage(lang);

    // 모바일 메뉴가 열려있는 경우 메뉴를 닫음
    if (show) {
      // 메뉴 닫기
      setShow(false);

      // body 스타일 완전히 초기화
      document.body.classList.remove('menu-open');
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.paddingRight = '';
      document.documentElement.classList.remove('no-scroll');
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';

      // 스크롤 위치 복원 - 메뉴가 열릴 때 스크롤 위치가 변경되지 않도록 했으므로 여기서는 필요 없음
      // if (scrollPosition.current > 0) {
      //     window.scrollTo(0, scrollPosition.current);
      // }
    }
  };

  const handleClick = (e, url) => {
    e.preventDefault(); // 기본 동작 방지

    const isMobile = window.innerWidth <= 800; // 모바일 여부 확인 (800px 기준)
    const element = document.querySelector(url);

    if (isMobile && show) {
      // 모바일에서 메뉴가 열려있는 경우

      // 현재 스크롤 위치 저장
      const currentPosition = scrollPosition.current;

      // 모바일 메뉴 닫기
      setShow(false);
      setIsVisible(false);

      // body 스타일 완전히 초기화
      document.body.classList.remove('menu-open');
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.paddingRight = '';
      document.documentElement.classList.remove('no-scroll');
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';

      if (element) {
        // URL에 해시 추가
        window.history.pushState(null, '', url);

        // 약간의 지연 후 스크롤 실행 (메뉴 닫힘 후)
        setTimeout(() => {
          // 스크롤 위치 복원 없이 바로 목표 위치로 스크롤
          element.scrollIntoView({ behavior: 'smooth' });
        }, 300); // 메뉴 닫힘 애니메이션이 완료될 때까지 충분히 기다림
      } else {
        // 요소가 없는 경우 스크롤 위치 복원
        if (currentPosition > 0) {
          window.scrollTo(0, currentPosition);
        }
      }
    } else {
      // PC 버전이거나 모바일에서 메뉴가 닫혀있는 경우
      if (element) {
        // URL에 해시 추가
        window.history.pushState(null, '', url);

        // 바로 부드럽게 스크롤
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // 메뉴 토글 함수
  const toggleMenu = () => {
    if (show) {
      // 메뉴 닫기
      setShow(false);

      // body 스타일 완전히 초기화
      document.body.classList.remove('menu-open');
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.style.paddingRight = '';
      document.documentElement.classList.remove('no-scroll');
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';

      // 스크롤 위치 복원 - 메뉴가 열릴 때 스크롤 위치가 변경되지 않도록 했으므로 여기서는 필요 없음
      // if (scrollPosition.current > 0) {
      //     window.scrollTo(0, scrollPosition.current);
      // }
    } else {
      // 메뉴 열기
      setShow(true);

      // 현재 스크롤 위치 저장 (메뉴가 닫힐 때 복원하기 위함)
      scrollPosition.current = window.pageYOffset;

      // body에 메뉴 열림 클래스 추가 (스크롤 방지)
      document.body.classList.add('menu-open');
      document.documentElement.classList.add('no-scroll');

      // 중요: position: fixed와 top 속성을 사용하지 않음
      // 이렇게 하면 스크롤 위치가 유지됨

      // 스크롤 방지만 설정하고 위치는 변경하지 않음
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    }
  };

  // 오버레이 클릭 핸들러
  const handleOverlayClick = (e) => {
    // 이벤트 전파 중지 (버블링 방지)
    e.stopPropagation();

    // 메뉴 닫기
    setShow(false);

    // body 스타일 완전히 초기화
    document.body.classList.remove('menu-open');
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.height = '';
    document.body.style.paddingRight = '';
    document.documentElement.classList.remove('no-scroll');
    document.documentElement.style.overflow = '';
    document.documentElement.style.height = '';

    // 스크롤 위치 복원 - 메뉴가 열릴 때 스크롤 위치가 변경되지 않도록 했으므로 여기서는 필요 없음
    // if (scrollPosition.current > 0) {
    //     window.scrollTo(0, scrollPosition.current);
    // }
  };

  useEffect(() => {
    // 섹션들을 관찰하는 Intersection Observer 설정
    const observer = new IntersectionObserver(
      (entries) => {
        // 현재 보이는 섹션들 중 가장 많이 보이는 섹션을 찾기 위한 변수
        let maxVisibleSection = null;
        let maxVisibleRatio = 0;

        entries.forEach((entry) => {
          // 현재 보이는 섹션의 비율 계산
          const visibleRatio = entry.intersectionRatio;

          // 가장 많이 보이는 섹션 업데이트
          if (entry.isIntersecting && visibleRatio > maxVisibleRatio) {
            maxVisibleRatio = visibleRatio;
            maxVisibleSection = entry.target;
          }
        });

        // 가장 많이 보이는 섹션이 있으면 URL 해시 업데이트
        if (maxVisibleSection) {
          const id = maxVisibleSection.getAttribute('id');
          if (id) {
            window.history.replaceState(null, '', `#${id}`);

            // 네비게이션 메뉴 활성화 상태 업데이트
            document.querySelectorAll('nav a').forEach((link) => {
              link.classList.remove('active');
              if (link.getAttribute('href') === `#${id}`) {
                link.classList.add('active');
              }
            });
          }
        }
      },
      {
        threshold: [0.1, 0.2, 0.3, 0.4, 0.5], // 여러 threshold 값 사용
        rootMargin: '-80px 0px', // 상단 여백 조정
      }
    );

    // 모든 섹션 요소 관찰 시작
    document.querySelectorAll('section[id]').forEach((section) => {
      observer.observe(section);
    });

    // 특별 처리가 필요한 섹션들
    const specialSections = ['team', 'menu', 'reservation', 'contact'];

    // 각 특별 섹션에 대한 별도의 observer 설정
    const specialObservers = {};

    specialSections.forEach((sectionId) => {
      const section = document.getElementById(sectionId);
      if (section) {
        const sectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                window.history.replaceState(null, '', `#${sectionId}`);
              }
            });
          },
          {
            threshold: 0.15, // 더 낮은 threshold 값 사용
            rootMargin: '-50px 0px',
          }
        );

        sectionObserver.observe(section);
        specialObservers[sectionId] = sectionObserver;
      }
    });

    return () => {
      observer.disconnect();
      // 특별 observer들도 해제
      Object.values(specialObservers).forEach((obs) => obs.disconnect());
    };
  }, []);

  // 스크롤 이벤트 처리
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;

      // 스크롤 위치에 따라 헤더 표시/숨김 처리
      if (scrollTop > 50) {
        if (scrollTop > lastScrollTop.current && scrollTop > 300) {
          setIsVisible(false);
        } else {
          setIsVisible(true);
        }
      } else {
        setIsVisible(true);
      }

      // 현재 스크롤 위치 저장
      lastScrollTop.current = scrollTop;

      // 스크롤 위치에 따른 섹션 감지 및 해시 업데이트
      // 디바운스 처리를 위한 타이머 설정
      if (window.scrollUpdateTimer) {
        clearTimeout(window.scrollUpdateTimer);
      }

      window.scrollUpdateTimer = setTimeout(() => {
        const sections = document.querySelectorAll('section[id]');
        let currentSection = '';
        let maxVisibleRatio = 0;

        sections.forEach((section) => {
          const rect = section.getBoundingClientRect();
          const windowHeight = window.innerHeight;

          // 섹션이 화면에 얼마나 보이는지 계산
          const visibleHeight =
            Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
          const sectionHeight = rect.height;
          const visibleRatio = visibleHeight / sectionHeight;

          // 가장 많이 보이는 섹션 찾기
          if (visibleRatio > 0 && visibleRatio > maxVisibleRatio) {
            maxVisibleRatio = visibleRatio;
            currentSection = section.getAttribute('id');
          }
        });

        // 특별 섹션들에 대한 추가 처리
        const specialSections = {
          team: { offset: -150 },
          menu: { offset: -100 },
          reservation: { offset: -100 },
          contact: { offset: -100 },
        };

        // 특별 섹션 처리
        Object.entries(specialSections).forEach(([sectionId, config]) => {
          const section = document.getElementById(sectionId);
          if (section) {
            const rect = section.getBoundingClientRect();
            const adjustedTop = rect.top - config.offset;

            // 특별 섹션이 화면에 보이는지 확인 (조정된 위치 기준)
            if (adjustedTop < window.innerHeight / 2 && rect.bottom > 0) {
              currentSection = sectionId;
            }
          }
        });

        // 현재 섹션이 있으면 URL 해시 업데이트
        if (currentSection && currentSection !== '') {
          window.history.replaceState(null, '', `#${currentSection}`);
        }
      }, 100); // 100ms 디바운스
    };

    // 스크롤 이벤트 리스너 등록 (passive: true로 설정하여 성능 향상)
    window.addEventListener('scroll', handleScroll, { passive: true });

    // 컴포넌트 언마운트 시 이벤트 리스너 제거 및 타이머 정리
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (window.scrollUpdateTimer) {
        clearTimeout(window.scrollUpdateTimer);
      }
    };
  }, []);

  // 화면 크기 변경 감지
  useEffect(() => {
    const handleResize = () => {
      // 모바일 메뉴가 열려있는 상태에서 화면 크기가 PC 크기로 변경되면 메뉴 닫기
      if (show && window.innerWidth > 800) {
        setShow(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [show]);

  return (
    <header
      className={`header__wrap ${!isVisible ? 'hide' : ''} ${show ? 'menu-open' : ''}`}
      id="header"
      role="banner"
    >
      <div className="header__inner">
        <div className="header__logo">
          <a href="#intro" onClick={(e) => handleClick(e, '#intro')}>
            MOSU
          </a>
        </div>
        <nav
          className={`header__nav ${show ? 'show' : ''}`}
          role="navigation"
          aria-label="메인 메뉴"
          ref={menuRef}
          style={
            show
              ? {
                  position: 'fixed', // absolute에서 fixed로 변경
                  top: '60px',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: 'calc(100vh - 60px)',
                  zIndex: 10000,
                }
              : {}
          }
        >
          <ul>
            {headerNav.map((nav, key) => (
              <li
                key={key}
                onClick={(e) => handleClick(e, nav.url)}
                style={{ cursor: 'pointer' }}
              >
                <span>{nav.title}</span>
              </li>
            ))}
            <li className="language-toggle">
              <button
                onClick={() => handleLanguageChange('kr')}
                className={language === 'kr' ? 'active' : ''}
                data-lang="kr"
              >
                KR
              </button>
              <button
                onClick={() => handleLanguageChange('en')}
                className={language === 'en' ? 'active' : ''}
                data-lang="en"
              >
                EN
              </button>
            </li>
          </ul>
        </nav>
        <div
          className="header__nav__mobile"
          id="headerToggle"
          aria-controls="primary-menu"
          aria-expanded={show ? 'true' : 'false'}
          role="button"
          onClick={toggleMenu}
          tabIndex="0"
        >
          <span></span>
        </div>
      </div>
      {/* 메뉴가 열렸을 때 표시되는 오버레이 */}
      {show && (
        <div
          className="menu-overlay"
          onClick={handleOverlayClick}
          style={{
            position: 'fixed', // absolute에서 fixed로 변경
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
          }}
        ></div>
      )}
    </header>
  );
};

export default Header;
